import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import {
  Ticket, Plus, Copy, Trash2, Search, CheckCircle2, XCircle,
  Loader2, Tag, Calendar, Users, Gift,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-gray-900/80 border-gray-800" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-black text-white">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function AdminCoupons() {
  const { token } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, used: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    description: 'Cupon promocional - 1 mes gratis',
    plan_id: 'pro',
    duration_days: 30,
    quantity: 5,
    expires_days: 90,
  });

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/coupons`, { headers: headers() });
      setCoupons(res.data.coupons || []);
      setStats({
        total: res.data.total || 0,
        active: res.data.active || 0,
        used: res.data.used || 0,
      });
    } catch (e) {
      toast.error('Error al cargar cupones');
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { if (token) fetchCoupons(); }, [token, fetchCoupons]);

  const handleCreate = async () => {
    if (!form.description.trim() || form.quantity < 1) {
      toast.error('Completa todos los campos');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/admin/coupons/create`,
        {
          description: form.description,
          discount_type: 'free_month',
          plan_id: form.plan_id,
          duration_days: Number(form.duration_days),
          quantity: Number(form.quantity),
          expires_days: Number(form.expires_days),
        },
        { headers: headers() }
      );
      toast.success(res.data.message);
      setDialogOpen(false);
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear cupones');
    }
    setCreating(false);
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`¿Desactivar el cupón ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: headers() });
      toast.success('Cupón desactivado');
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Código ${code} copiado`);
  };

  const filtered = coupons.filter(c => {
    if (filter === 'active' && (c.used || c.status !== 'active')) return false;
    if (filter === 'used' && !c.used) return false;
    if (filter === 'referral' && c.type !== 'referral') return false;
    if (filter === 'promo' && c.type !== 'promotional') return false;
    if (search && !c.code?.toLowerCase().includes(search.toLowerCase()) &&
        !c.description?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <AdminLayout title="Cupones y Promociones" subtitle="Gestiona códigos promocionales y referidos">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Cupones" value={stats.total} icon={Ticket} color="bg-purple-500/20 text-purple-400" />
        <StatCard label="Activos" value={stats.active} icon={CheckCircle2} color="bg-green-500/20 text-green-400" />
        <StatCard label="Usados" value={stats.used} icon={Gift} color="bg-amber-500/20 text-amber-400" />
      </div>

      {/* Actions Bar */}
      <Card className="bg-gray-900 border-gray-800 mb-6">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código o descripción..."
              className="pl-9 bg-gray-950 border-gray-800 text-white"
              data-testid="search-coupons"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-gray-950 border-gray-800 text-white" data-testid="filter-coupons">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="all" className="text-white hover:bg-gray-800">Todos</SelectItem>
              <SelectItem value="active" className="text-white hover:bg-gray-800">Activos</SelectItem>
              <SelectItem value="used" className="text-white hover:bg-gray-800">Usados</SelectItem>
              <SelectItem value="promo" className="text-white hover:bg-gray-800">Promocionales</SelectItem>
              <SelectItem value="referral" className="text-white hover:bg-gray-800">Por Referido</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
            data-testid="create-coupons-btn"
          >
            <Plus size={16} className="mr-1" />
            Crear Cupones
          </Button>
        </CardContent>
      </Card>

      {/* Coupons List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-purple-400" size={32} />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="py-12 text-center">
            <Ticket size={40} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500">No hay cupones</p>
            <p className="text-gray-600 text-sm mt-1">Crea cupones promocionales con el botón arriba</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="coupons-list">
          {filtered.map((c) => (
            <Card key={c.id || c.code} className={`border ${c.used ? 'bg-gray-900/40 border-gray-800' : c.status === 'deactivated' ? 'bg-red-900/10 border-red-900/30' : 'bg-gray-900 border-gray-800'}`}
              data-testid={`coupon-row-${c.code}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  {/* Code + Type */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      c.type === 'referral' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {c.type === 'referral' ? <Users size={18} /> : <Tag size={18} />}
                    </div>
                    <div className="min-w-0">
                      <button
                        onClick={() => copyCode(c.code)}
                        className="font-mono font-bold text-white hover:text-emerald-400 transition-colors flex items-center gap-1 text-sm"
                        data-testid={`copy-code-${c.code}`}
                      >
                        {c.code}
                        <Copy size={12} className="opacity-50" />
                      </button>
                      <p className="text-gray-500 text-xs truncate max-w-md">{c.description}</p>
                    </div>
                  </div>

                  {/* Plan + Duration */}
                  <div className="flex gap-2 flex-wrap text-xs">
                    <Badge variant="outline" className="border-gray-700 text-gray-400">
                      Plan: {c.plan_id || '—'}
                    </Badge>
                    <Badge variant="outline" className="border-gray-700 text-gray-400">
                      {c.duration_days || 0} días
                    </Badge>
                    {c.expires_at && (
                      <Badge variant="outline" className="border-gray-700 text-gray-500 hidden sm:inline-flex">
                        <Calendar size={10} className="mr-1" />
                        Exp: {new Date(c.expires_at).toLocaleDateString('es-DO')}
                      </Badge>
                    )}
                  </div>

                  {/* Status + Actions */}
                  <div className="flex items-center gap-2">
                    {c.used ? (
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                        <CheckCircle2 size={10} className="mr-1" /> Usado
                      </Badge>
                    ) : c.status === 'deactivated' ? (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        <XCircle size={10} className="mr-1" /> Desactivado
                      </Badge>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                        Activo
                      </Badge>
                    )}
                    {!c.used && c.status !== 'deactivated' && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(c.id, c.code)}
                        className="h-8 w-8 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-coupon-${c.code}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white" data-testid="create-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus size={20} className="text-green-400" />
              Crear Cupones Promocionales
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Descripción</label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-gray-950 border-gray-800 text-white"
                data-testid="form-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Plan</label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger className="bg-gray-950 border-gray-800 text-white" data-testid="form-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800">
                    <SelectItem value="basic" className="text-white hover:bg-gray-800">Básico</SelectItem>
                    <SelectItem value="pro" className="text-white hover:bg-gray-800">Pro</SelectItem>
                    <SelectItem value="vip" className="text-white hover:bg-gray-800">VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Duración (días)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.duration_days}
                  onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                  className="bg-gray-950 border-gray-800 text-white"
                  data-testid="form-duration"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Cantidad</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="bg-gray-950 border-gray-800 text-white"
                  data-testid="form-quantity"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Expira en (días)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.expires_days}
                  onChange={(e) => setForm({ ...form, expires_days: e.target.value })}
                  className="bg-gray-950 border-gray-800 text-white"
                  data-testid="form-expires"
                />
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
              Se generarán <strong>{form.quantity || 0}</strong> códigos únicos tipo
              <code className="ml-1 px-1.5 py-0.5 bg-black/40 rounded">PROMO-XXXX-XXXX</code>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
              data-testid="confirm-create-btn"
            >
              {creating ? <Loader2 size={16} className="animate-spin mr-1" /> : <Plus size={16} className="mr-1" />}
              Generar Cupones
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
